import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { ArrowLeft, ArrowRight, InstagramLogo } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Photo, Seo } from "../../kit.jsx";
import { SITE_URL } from "../../data/seo.js";
import { MEETINGS } from "./data.js";
import { StNotFound } from "./shared.jsx";
import "./stories.css";

function Fact({ term, children, index }) {
  const reduce = useReducedMotion();
  return (
    <div className="st-fact">
      <motion.span
        className="st-fact__rule"
        aria-hidden="true"
        initial={reduce ? false : { scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.9, delay: 0.35 + index * 0.08, ease: EASE_OUT }}
      />
      <dt>{term}</dt>
      <motion.dd
        initial={reduce ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5 + index * 0.08, ease: EASE_OUT }}
      >
        {children}
      </motion.dd>
    </div>
  );
}

function Record({ meeting }) {
  const reduce = useReducedMotion();
  const index = MEETINGS.findIndex((item) => item.slug === meeting.slug);
  const previous = MEETINGS[(index - 1 + MEETINGS.length) % MEETINGS.length];
  const next = MEETINGS[(index + 1) % MEETINGS.length];
  const facts = [
    ["Who", meeting.name],
    ["Role", meeting.role],
    ["Date", meeting.date],
    ["Place", meeting.place],
    ["Attendees", meeting.attendees],
    ["Purpose", meeting.context],
  ].filter(([, value]) => value);

  return (
    <main className="st-rec">
      <nav className="st-rec__top" aria-label="Meeting archive">
        <Link className="st-crumb" to="/meetings"><ArrowLeft weight="bold" aria-hidden="true" /> All meeting records</Link>
        <span className="st-mono">Record {meeting.number} of {MEETINGS.length}</span>
      </nav>

      <header className="st-rec__head">
        <p className="st-mono">Meeting record</p>
        <MaskWords as="h1" text={meeting.name} onMount delay={0.05} />
        <motion.div
          className={`st-rec__status ${meeting.status === "Programme cohort" ? "is-cohort" : ""}`}
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE_OUT }}
        >
          <span className="st-status">
            <span className="st-status__key">Status</span>
            <span>{meeting.status}</span>
          </span>
          <p>{meeting.statusNote}</p>
        </motion.div>
      </header>

      <div className="st-rec__grid">
        <figure className="st-rec__photo">
          <motion.div
            className="st-rec__frame"
            initial={reduce ? false : { clipPath: "inset(0% 100% 0% 0% round 28px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.65, 0, 0.35, 1] }}
          >
            <Photo src={meeting.image} alt={meeting.alt} sizes="(max-width: 900px) 100vw, 50vw" priority />
          </motion.div>
        </figure>

        <div className="st-rec__panel">
          <dl className="st-facts">
            {facts.map(([term, value], i) => (
              <Fact key={term} term={term} index={i}>{value}</Fact>
            ))}
          </dl>
          <a className="st-rec__source" href={meeting.sourceUrl} target="_blank" rel="noreferrer">
            <InstagramLogo weight="bold" aria-hidden="true" />
            <span><strong>View on Instagram</strong><small>The original post by @tani.agg</small></span>
          </a>
        </div>
      </div>

      <div className="st-rec__notes">
        <section aria-labelledby="st-rec-discussed">
          <h2 id="st-rec-discussed">What was discussed</h2>
          <p>{meeting.context}</p>
        </section>
        <section aria-labelledby="st-rec-next">
          <h2 id="st-rec-next">What happened next</h2>
          {meeting.next ? (
            <>
              <p>GRID's role in Metamorphosis, and what the programme did, is set out on the programme page.</p>
              <Link className="gh-link" to={meeting.next.to}>{meeting.next.label} <ArrowRight weight="bold" aria-hidden="true" /></Link>
            </>
          ) : (
            <>
              <p>A meeting is not an endorsement or a partnership. Partners are listed under the programme they belong to.</p>
              <Link className="gh-link" to="/partnerships">See our partners <ArrowRight weight="bold" aria-hidden="true" /></Link>
            </>
          )}
        </section>
      </div>

      <nav className="st-rec__pager" aria-label="Other meeting records">
        {[
          ["Previous record", previous, ArrowLeft],
          ["Next record", next, ArrowRight],
        ].map(([label, item, Icon]) => (
          <Link key={label} to={`/meetings/${item.slug}`} className={`st-rec__pager-link ${label === "Next record" ? "is-next" : ""}`}>
            <span className="st-rec__pager-img"><Photo src={item.image} alt="" sizes="160px" /></span>
            <span className="st-rec__pager-copy">
              <span className="st-mono"><Icon weight="bold" aria-hidden="true" /> {label}</span>
              <strong>{item.name}</strong>
              <span>{item.role}</span>
            </span>
          </Link>
        ))}
      </nav>
    </main>
  );
}

export default function MeetingRecordPage() {
  const { slug } = useParams();
  const meeting = MEETINGS.find((item) => item.slug === slug);
  if (!meeting) return <StNotFound back="/meetings" label="All meeting records" />;

  const meetingSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: meeting.name,
    description: meeting.context,
    image: `${SITE_URL}${meeting.image}`,
    datePublished: "2026-07-10",
    author: { "@type": "Person", name: "Tanishq Aggarwal" },
  };

  return (
    <PageFrame className="rd-page st-page">
      <Seo
        title={`${meeting.name} · Meeting Record`}
        path={`/meetings/${meeting.slug}`}
        description={meeting.context}
        type="article"
        schema={meetingSchema}
      />
      <Record meeting={meeting} key={meeting.slug} />
    </PageFrame>
  );
}
